// import { flash } from "@/components/toast/flash-message";
import { clearStorageItem } from '@/utils/hooks/useLocalStorage'
import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import Cookies from 'js-cookie'
import { resetAuth } from '../auth/authSlice'
import { toast } from 'sonner'

const toastError = (errors: any) => {
  const errorKeys = Object.keys(errors)
  if (Array.isArray(errors[errorKeys[0]])) {
    const message = errors[errorKeys[0]][0]
    toast.error(message)
    return
  } else {
    const message = errors[errorKeys[0]]
    toast.error(message)
  }
}

// API path appended to the host from VITE_PUBLIC_URL
const API_PATH = '/api/v1/admin'

// Resolve the base URL for the active environment.
// - In dev: use a relative path so Vite's dev proxy can forward to the
//   configured host (bypasses CORS).
// - In builds (staging/production): call the host directly using
//   VITE_PUBLIC_URL set via `.env.development`, `.env.staging`, or
//   `.env.production` (selected by `vite build --mode <env>`).
const getBaseUrl = () => {
  if (import.meta.env.DEV) {
    return API_PATH
  }

  const host = (import.meta.env.VITE_PUBLIC_URL ?? '').replace(/\/+$/, '')
  if (!host) {
    throw new Error(
      'VITE_PUBLIC_URL is not set. Define it in .env.<mode> or your hosting env vars.',
    )
  }
  return `${host}${API_PATH}`
}

export const baseQuery = fetchBaseQuery({
  baseUrl: getBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const accessToken: string = Cookies.get('token') || ''
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }
    headers.set('accept', 'application/json')
    return headers
  },
})

export const baseQueryInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)
  // console.log(result)

  // const searchParams = useSearchParams();
  // const redirect_url = searchParams.get("redirect");

  if (result?.error) {
    const res: any = result.error
    if (res.status === 403) {
      if (res?.data?.message) {
        toast.error(res?.data?.message)
      }
      if (res?.data?.error?.message) {
        toast.error(res?.data?.error?.message)
      }
      // api.dispatch(resetAuth());
      // Cookies.remove("token");
      // clearStorageItem();
      // window.location.href = "/login";
    }
    if (res?.status === 401) {
      if (res?.data?.status === 'failed') {
        api.dispatch(resetAuth())
        Cookies.remove('token')
        clearStorageItem()
        if (typeof window && typeof window !== undefined) {
          const params = new URLSearchParams(window.location.search)
          const redirect = params.get('redirect')
          const loginUrl = `/login?redirect=${encodeURIComponent(
            redirect || '',
          )}`
          window.location.href = loginUrl
        }
        window.location.href = '/login'
      }
    }
    if (res.status === 404) {
      const message = res?.data?.error?.message || res?.data?.message
      if (message) {
        toast.error(message)
      }
    }
    if (res.status === 422) {
      if (res?.data?.errors) {
        toastError(res.data.errors)
      }
      if (res?.data?.error) {
        const message = res.data.error.message
        console.log(message)
        // toast.error(message);
      }
    }

    if (res.status === 409) {
      const message = res?.data?.message
      toast.error(message)
    }

    if (res.status === 503) {
      const message = res?.data?.message || res?.data?.error?.message
      if (message) {
        toast.error(message)
      }
    }

    if (res.status === 500) {
      const message = res?.data?.message
      console.log(message)
      // toast.error(message);
    }

    if (res.status === 400) {
      let message = res?.data?.message
      console.log(res)
      if (typeof message === 'object') {
        message = res?.data?.error?.message?.error || JSON.stringify(message)
      }
      toast.error(message)
    }
  }

  return result
}

export const TAG_TYPES = [
  'Application',
  'Admin-role',
  'Admin-permissions',
  'Vendors',
  'Menu-Categories',
  'Business-Types',
  'Complaints',
  'Commissions',
  'CustomerTransactions',
  'VendorTransactions',
  'Delivery',
]

export const baseApi = createApi({
  baseQuery: baseQueryInterceptor,
  endpoints: () => ({}),
  reducerPath: 'baseApi',
  tagTypes: TAG_TYPES,
})
